<script setup>
/**
 * The one credential this whole section ever renders, shown once.
 *
 * WHY "ONCE" IS LITERAL AND NOT A UX FLOURISH: the store holds a one-way sha256 of the
 * key (`mintApiKey`, `src/db/api-key-store.ts`), so there is no read path anywhere in the
 * product that can produce the plaintext again — not this page, not the API, not an
 * operator with psql. The mint's response is the only place it has ever existed outside
 * the process that generated it. A dialog that says "copy this now" is usually a nag; here
 * it is the literal truth, and the copy says so three times over — before, during and
 * after — because an owner who misses it has to turn the key off and start again.
 *
 * WHERE THE SECRET IS ALLOWED TO GO: into this component's `secret` prop, into the
 * clipboard when the owner asks, and nowhere else. It is never put in the keys list (whose
 * rows are built by a reader that has no such field), never in a toast, and never in a
 * log line — including a `console.error` inside the copy handler, which is why the catch
 * below reports a translated sentence rather than the thrown error.
 *
 * HIDDEN BY DEFAULT, AND COPYABLE WHILE HIDDEN. The commonest place this screen gets read
 * is a laptop in a shop with customers on the other side of the counter. Revealing is a
 * deliberate second action; copying does not require it.
 *
 * THE MASK IS A FIXED RUN OF DOTS, not one dot per character — a mask that leaks the
 * length is a mask that helps.
 */
import { computed, onScopeDispose, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';

import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** The plaintext, straight from the mint and held nowhere else. */
  secret: { type: String, required: true },
  /** The readable head of the key, so the panel names the same key the list will show. */
  keyPrefix: { type: String, default: '' },
});

const { t } = useWasloI18n();

/** Fixed length, so the mask cannot leak the key's own — see above. */
const MASK = '••••••••••••••••••••••••';

const isRevealed = ref(false);
/** `'idle' | 'copied' | 'failed'` — the button's own answer, not a page-level toast. */
const copyState = ref('idle');

let resetTimer = null;

const clearTimer = () => {
  if (resetTimer !== null) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
};

onScopeDispose(clearTimer);

const shown = computed(() =>
  isRevealed.value ? props.secret : `${props.keyPrefix}${MASK}`
);

const copy = async () => {
  clearTimer();
  try {
    await copyTextToClipboard(props.secret);
    copyState.value = 'copied';
  } catch {
    // Deliberately swallowed: the thrown error's message is built by the clipboard helper
    // from the browser's own, and this is the one handler in the section whose `error`
    // argument would be adjacent to a credential. Nothing is logged.
    copyState.value = 'failed';
  }
  resetTimer = setTimeout(() => {
    copyState.value = 'idle';
    resetTimer = null;
  }, 4000);
};
</script>

<template>
  <div class="flex flex-col gap-4" data-waslo-outbound-secret>
    <Banner color="amber" data-waslo-outbound-secret-once>
      {{ t('WASLO.OUTBOUND.KEYS.SECRET.ONCE') }}
    </Banner>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('WASLO.OUTBOUND.KEYS.SECRET.LABEL') }}
      </span>
      <!--
        `break-all` + its own box, not an input: README §2 — an unbroken token of this
        length stretched a page to 2.6x the viewport on a phone. `dir="ltr"` because the
        key is an identifier, not text, and it must not be reordered inside an Arabic page.
        A `<p>` rather than a control because there is nothing here to edit.
      -->
      <p
        class="mb-0 px-3 py-2 font-mono text-sm break-all outline outline-1 -outline-offset-1 rounded-lg outline-n-weak bg-n-alpha-black2 text-n-slate-12"
        dir="ltr"
        data-waslo-outbound-secret-value
      >
        {{ shown }}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <Button
        :label="
          copyState === 'copied'
            ? t('WASLO.OUTBOUND.KEYS.SECRET.COPIED')
            : t('WASLO.OUTBOUND.KEYS.SECRET.COPY')
        "
        :icon="copyState === 'copied' ? 'i-lucide-check' : 'i-lucide-copy'"
        variant="solid"
        color="blue"
        size="sm"
        data-waslo-outbound-secret-copy
        @click="copy"
      />
      <Button
        :label="
          isRevealed
            ? t('WASLO.OUTBOUND.KEYS.SECRET.HIDE')
            : t('WASLO.OUTBOUND.KEYS.SECRET.REVEAL')
        "
        :icon="isRevealed ? 'i-lucide-eye-off' : 'i-lucide-eye'"
        variant="faded"
        color="slate"
        size="sm"
        data-waslo-outbound-secret-reveal
        @click="isRevealed = !isRevealed"
      />
    </div>

    <p
      v-if="copyState === 'failed'"
      class="mb-0 text-sm text-n-ruby-9"
      data-waslo-outbound-secret-copy-failed
    >
      {{ t('WASLO.OUTBOUND.COMMON.COPY_FAILED') }}
    </p>

    <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
      {{ t('WASLO.OUTBOUND.KEYS.SECRET.LOST') }}
    </p>
  </div>
</template>
